/**
 * Keeps the document's CSS variables in step with the theme store: active
 * theme (or its unsaved draft), night mode — including the scheduled kind,
 * re-checked exactly at the next boundary — the motion preference, and the
 * skin with its thickness and its sounds.
 */
import { useEffect, useState } from 'react'
import { useThemeStore } from '../../lib/theme/theme-store'
import { applyThemeState } from '../../lib/theme/theme-bootstrap'
import { applySkin } from '../../lib/theme/apply-theme'
import { msUntilNextBoundary } from '../../lib/theme/night'
import { loadSkin } from '../../lib/theme/skin'
import { turnSoundsOn } from '../../lib/sound/sound'

export function ThemeInjector() {
  const profile = useThemeStore((s) => s.getActiveProfile())
  const nightMode = useThemeStore((s) => s.nightMode)
  const schedule = useThemeStore((s) => s.nightSchedule)
  const lightMode = useThemeStore((s) => s.lightMode)
  const motion = useThemeStore((s) => s.motion)
  const skin = useThemeStore((s) => s.skin)
  const thickness = useThemeStore((s) => s.thickness)
  const sound = useThemeStore((s) => s.sound)
  // Bumped at each schedule boundary so the effect below re-evaluates.
  const [tick, setTick] = useState(0)

  useEffect(() => {
    applyThemeState(useThemeStore.getState())
  }, [profile, nightMode, schedule, lightMode, motion, tick])

  // The stylesheet first, then the attribute: turning a skin on never shows
  // half of it, and turning it off is immediate (the flat skin is built in).
  useEffect(() => {
    let current = true
    void loadSkin(skin).then(() => {
      if (current) applySkin(skin, thickness)
    })
    return () => { current = false }
  }, [skin, thickness])

  // Fichero's sounds: their player is downloaded only while both are on.
  useEffect(() => {
    if (skin !== 'fichero' || !sound) return
    return turnSoundsOn()
  }, [skin, sound])

  useEffect(() => {
    // Forced light mode wins over the schedule: nothing to re-check meanwhile.
    if (nightMode !== 'schedule' || lightMode) return
    const timer = setTimeout(() => setTick((n) => n + 1), msUntilNextBoundary(schedule, new Date()) + 1000)
    return () => clearTimeout(timer)
  }, [nightMode, schedule, lightMode, tick])

  return null
}
